import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../AdminLayout";
import { API_BASE } from "@/config/api";

interface EmailStatus {
  smtp_host: string;
  smtp_port: number;
  smtp_security: "ssl" | "starttls" | "none";
  smtp_user: string;
  smtp_password_set: boolean;
  from_email: string;
  from_name: string;
  notify_enabled: boolean;
  notify_to: string;
  notify_subject: string;
  autoreply_enabled: boolean;
  autoreply_subject: string;
  autoreply_body: string;
  last_ok_at: string | null;
  last_error: string;
  last_error_at: string | null;
  defaults: { notify_subject: string; autoreply_subject: string; autoreply_body: string };
}

type Security = EmailStatus["smtp_security"];

const PRESETS: { label: string; host: string; port: string; security: Security }[] = [
  { label: "Gmail", host: "smtp.gmail.com", port: "465", security: "ssl" },
  { label: "Outlook / Microsoft 365", host: "smtp.office365.com", port: "587", security: "starttls" },
  { label: "Hostinger", host: "smtp.hostinger.com", port: "465", security: "ssl" },
];

const inputCls = "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm";
const labelCls = "block text-xs font-medium text-foreground-600 mb-1";
const cardCls = "rounded-lg border border-background-200 bg-background-50 p-5";
const secondaryBtnCls =
  "whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 disabled:opacity-60 cursor-pointer";

function Notice({ ok, text }: { ok: boolean; text: string }) {
  return (
    <div
      className={`mt-2 rounded-md p-3 text-sm border ${
        ok ? "bg-primary-100 border-primary-200 text-primary-800" : "bg-accent-100 border-accent-200 text-accent-800"
      }`}
    >
      {ok ? "✓ " : "✗ "}
      {text}
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" });
}

export default function AdminCorreoFormulario() {
  const [status, setStatus] = useState<EmailStatus | null>(null);
  const [host, setHost] = useState("");
  const [port, setPort] = useState("587");
  const [security, setSecurity] = useState<Security>("starttls");
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("");
  const [notifyEnabled, setNotifyEnabled] = useState(false);
  const [notifyTo, setNotifyTo] = useState("");
  const [notifySubject, setNotifySubject] = useState("");
  const [autoreplyEnabled, setAutoreplyEnabled] = useState(false);
  const [autoreplySubject, setAutoreplySubject] = useState("");
  const [autoreplyBody, setAutoreplyBody] = useState("");
  const [testTo, setTestTo] = useState("");

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<null | "save" | "verify" | "send">(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [verifyResult, setVerifyResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [sendResult, setSendResult] = useState<{ ok: boolean; text: string } | null>(null);

  function applyStatus(s: EmailStatus) {
    setStatus(s);
    setHost(s.smtp_host);
    setPort(String(s.smtp_port));
    setSecurity(s.smtp_security);
    setUser(s.smtp_user);
    setFromEmail(s.from_email);
    setFromName(s.from_name);
    setNotifyEnabled(s.notify_enabled);
    setNotifyTo(s.notify_to);
    setNotifySubject(s.notify_subject || s.defaults.notify_subject);
    setAutoreplyEnabled(s.autoreply_enabled);
    setAutoreplySubject(s.autoreply_subject || s.defaults.autoreply_subject);
    setAutoreplyBody(s.autoreply_body || s.defaults.autoreply_body);
  }

  useEffect(() => {
    fetch(`${API_BASE}/integrations/email`)
      .then((res) => res.json())
      .then(applyStatus)
      .finally(() => setLoading(false));
  }, []);

  // Guarda el formulario completo. Devuelve true si quedó guardado.
  async function save(): Promise<boolean> {
    setMessage(null);
    const portNumber = Number(port.trim());
    if (!Number.isInteger(portNumber) || portNumber < 1 || portNumber > 65535) {
      setMessage({ ok: false, text: "El puerto debe ser un número entre 1 y 65535 (por ejemplo 587 o 465)." });
      return false;
    }
    const payload: Record<string, unknown> = {
      smtp_host: host,
      smtp_port: portNumber,
      smtp_security: security,
      smtp_user: user,
      from_email: fromEmail,
      from_name: fromName,
      notify_enabled: notifyEnabled,
      notify_to: notifyTo,
      // Si el texto es igual al original se guarda vacío: el sitio usa el original.
      notify_subject: notifySubject === status?.defaults.notify_subject ? "" : notifySubject,
      autoreply_enabled: autoreplyEnabled,
      autoreply_subject: autoreplySubject === status?.defaults.autoreply_subject ? "" : autoreplySubject,
      autoreply_body: autoreplyBody === status?.defaults.autoreply_body ? "" : autoreplyBody,
    };
    if (password) payload.smtp_password = password;
    try {
      const res = await fetch(`${API_BASE}/integrations/email`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        setMessage({ ok: false, text: json?.error || "No se pudo guardar" });
        return false;
      }
      applyStatus(json);
      setPassword("");
      return true;
    } catch {
      setMessage({ ok: false, text: "Error de conexión." });
      return false;
    }
  }

  async function handleSave() {
    setBusy("save");
    setVerifyResult(null);
    setSendResult(null);
    if (await save()) setMessage({ ok: true, text: "Configuración guardada." });
    setBusy(null);
  }

  async function handleVerify() {
    setBusy("verify");
    setVerifyResult(null);
    setSendResult(null);
    try {
      if (!(await save())) return;
      const res = await fetch(`${API_BASE}/integrations/email/test`, { method: "POST" });
      const json = await res.json().catch(() => null);
      setVerifyResult(
        res.ok && json?.ok
          ? { ok: true, text: "Conexión correcta: el servidor aceptó el usuario y la contraseña." }
          : { ok: false, text: json?.error || "No se pudo conectar con el servidor de correo." }
      );
    } catch {
      setVerifyResult({ ok: false, text: "Error de conexión con el servidor." });
    } finally {
      setBusy(null);
    }
  }

  async function handleSendTest() {
    setBusy("send");
    setVerifyResult(null);
    setSendResult(null);
    try {
      if (!(await save())) return;
      const res = await fetch(`${API_BASE}/integrations/email/send-test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: testTo }),
      });
      const json = await res.json().catch(() => null);
      setSendResult(
        res.ok && json?.ok
          ? { ok: true, text: `Correo de prueba enviado a ${testTo}. Revisa la bandeja de entrada (y la carpeta de spam).` }
          : { ok: false, text: json?.error || "No se pudo enviar el correo de prueba." }
      );
      const refreshed = await fetch(`${API_BASE}/integrations/email`).then((r) => r.json());
      setStatus(refreshed);
    } catch {
      setSendResult({ ok: false, text: "Error de conexión con el servidor." });
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <p className="text-sm text-foreground-500">Cargando...</p>
      </AdminLayout>
    );
  }

  const configured = Boolean(status?.smtp_host && status?.from_email);

  return (
    <AdminLayout>
      <h1 className="font-heading font-bold text-2xl text-foreground-950">Correo del formulario de contacto</h1>
      <p className="mt-1 text-sm text-foreground-500">
        Datos del servidor de correo (SMTP) para que te llegue un aviso cuando alguien llena el formulario y,
        opcionalmente, para responderle automáticamente. Los textos y campos del formulario se editan en{" "}
        <Link to="/admin/formulario" className="text-primary-600 hover:text-primary-700 underline">
          Formulario de contacto
        </Link>
        . Las solicitudes siempre quedan guardadas en{" "}
        <Link to="/admin/contactos" className="text-primary-600 hover:text-primary-700 underline">
          Contactos
        </Link>
        , aunque el correo falle.
      </p>

      <div className="mt-4 flex flex-wrap gap-4 text-xs">
        <span className={configured ? "text-primary-700" : "text-foreground-400"}>
          {configured ? "✓" : "○"} Servidor {configured ? "configurado" : "sin configurar"}
        </span>
        <span className={status?.smtp_password_set ? "text-primary-700" : "text-foreground-400"}>
          {status?.smtp_password_set ? "✓" : "○"} Contraseña {status?.smtp_password_set ? "guardada" : "no guardada"}
        </span>
        <span className={status?.notify_enabled ? "text-primary-700" : "text-foreground-400"}>
          {status?.notify_enabled ? "✓" : "○"} Aviso de solicitudes {status?.notify_enabled ? "activado" : "desactivado"}
        </span>
        <span className={status?.autoreply_enabled ? "text-primary-700" : "text-foreground-400"}>
          {status?.autoreply_enabled ? "✓" : "○"} Respuesta automática {status?.autoreply_enabled ? "activada" : "desactivada"}
        </span>
      </div>

      {status?.last_error && (
        <div className="mt-3 max-w-2xl rounded-md border border-accent-200 bg-accent-100 p-3 text-sm text-accent-800">
          <strong>Último envío fallido</strong> ({formatDate(status.last_error_at)}): {status.last_error}
        </div>
      )}
      {!status?.last_error && status?.last_ok_at && (
        <p className="mt-3 text-xs text-foreground-500">Último correo enviado con éxito: {formatDate(status.last_ok_at)}.</p>
      )}

      {message && (
        <div className="max-w-2xl">
          <Notice ok={message.ok} text={message.text} />
        </div>
      )}

      <div className="mt-6 space-y-5 max-w-2xl">
        <section className={`${cardCls} space-y-3`}>
          <h2 className="font-heading font-semibold text-foreground-900">Servidor de correo (SMTP)</h2>
          <div>
            <label className={labelCls}>Rellenar con los datos de un proveedor</label>
            <select
              className={inputCls}
              value=""
              onChange={(e) => {
                const p = PRESETS.find((x) => x.label === e.target.value);
                if (p) {
                  setHost(p.host);
                  setPort(p.port);
                  setSecurity(p.security);
                }
              }}
            >
              <option value="">Elegir (opcional)...</option>
              {PRESETS.map((p) => (
                <option key={p.label} value={p.label}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-3 sm:grid-cols-[1fr_110px]">
            <div>
              <label className={labelCls}>Servidor SMTP</label>
              <input className={inputCls} value={host} onChange={(e) => setHost(e.target.value)} placeholder="smtp.tudominio.cl" />
            </div>
            <div>
              <label className={labelCls}>Puerto</label>
              <input className={inputCls} inputMode="numeric" value={port} onChange={(e) => setPort(e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Seguridad de la conexión</label>
            <select className={inputCls} value={security} onChange={(e) => setSecurity(e.target.value as Security)}>
              <option value="ssl">SSL/TLS (normalmente puerto 465)</option>
              <option value="starttls">STARTTLS (normalmente puerto 587)</option>
              <option value="none">Sin cifrado (puerto 25, no recomendado)</option>
            </select>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Usuario</label>
              <input
                className={inputCls}
                value={user}
                onChange={(e) => setUser(e.target.value)}
                autoComplete="off"
                placeholder="Normalmente es el correo completo"
              />
            </div>
            <div>
              <label className={labelCls}>Contraseña</label>
              <input
                type="password"
                className={inputCls}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                placeholder={status?.smtp_password_set ? "•••••••• (guardada, déjala vacía para no cambiarla)" : "Contraseña del correo"}
              />
            </div>
          </div>
          <p className="text-xs text-foreground-500">
            Con Gmail y otros proveedores con verificación en dos pasos hay que crear una «contraseña de aplicación»;
            la contraseña normal no funciona. La contraseña se guarda en el servidor y nunca se vuelve a mostrar.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Correo remitente</label>
              <input
                className={inputCls}
                value={fromEmail}
                onChange={(e) => setFromEmail(e.target.value)}
                placeholder="contacto@tudominio.cl"
              />
            </div>
            <div>
              <label className={labelCls}>Nombre del remitente</label>
              <input className={inputCls} value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="ESPACIO BORMAN" />
            </div>
          </div>
          <p className="text-xs text-foreground-500">
            Casi todos los proveedores exigen que el remitente sea el mismo correo del usuario o uno de su dominio.
          </p>

          <div className="border-t border-background-200 pt-3">
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={handleVerify} disabled={busy !== null || !host.trim()} className={secondaryBtnCls}>
                {busy === "verify" ? "Probando conexión..." : "Probar conexión"}
              </button>
            </div>
            <p className="mt-1 text-xs text-foreground-500">
              Guarda los datos y hace una conexión real con el servidor para comprobar usuario y contraseña.
            </p>
            {verifyResult && <Notice ok={verifyResult.ok} text={verifyResult.text} />}

            <div className="mt-4 flex gap-2">
              <input
                className={inputCls}
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="Correo donde recibir la prueba"
              />
              <button
                type="button"
                onClick={handleSendTest}
                disabled={busy !== null || !host.trim() || !testTo.trim()}
                className={secondaryBtnCls}
              >
                {busy === "send" ? "Enviando..." : "Enviar correo de prueba"}
              </button>
            </div>
            {sendResult && <Notice ok={sendResult.ok} text={sendResult.text} />}
          </div>
        </section>

        <section className={`${cardCls} space-y-3`}>
          <h2 className="font-heading font-semibold text-foreground-900">Aviso cuando llega una solicitud</h2>
          <label className="inline-flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
            <input
              type="checkbox"
              className="accent-primary-500"
              checked={notifyEnabled}
              onChange={(e) => setNotifyEnabled(e.target.checked)}
            />
            Enviarme un correo por cada solicitud nueva
          </label>
          <div>
            <label className={labelCls}>Correos que reciben el aviso (separa varios con coma)</label>
            <input
              className={inputCls}
              value={notifyTo}
              onChange={(e) => setNotifyTo(e.target.value)}
              placeholder="ventas@tudominio.cl, gerencia@tudominio.cl"
            />
          </div>
          <div>
            <label className={labelCls}>Asunto del correo</label>
            <input className={inputCls} value={notifySubject} onChange={(e) => setNotifySubject(e.target.value)} />
            <p className="mt-1 text-xs text-foreground-500">
              Puedes usar {"{nombre}"}, {"{email}"}, {"{telefono}"}, {"{tipo_unidad}"}, {"{m2}"} y {"{sitio}"}. El correo
              trae todos los datos de la solicitud, y al responderlo le escribes directo a la persona.
            </p>
          </div>
        </section>

        <section className={`${cardCls} space-y-3`}>
          <h2 className="font-heading font-semibold text-foreground-900">Respuesta automática a quien escribe</h2>
          <label className="inline-flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
            <input
              type="checkbox"
              className="accent-primary-500"
              checked={autoreplyEnabled}
              onChange={(e) => setAutoreplyEnabled(e.target.checked)}
            />
            Enviar un correo de confirmación a la persona que llena el formulario
          </label>
          <div>
            <label className={labelCls}>Asunto</label>
            <input className={inputCls} value={autoreplySubject} onChange={(e) => setAutoreplySubject(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Mensaje</label>
            <textarea className={inputCls} rows={7} value={autoreplyBody} onChange={(e) => setAutoreplyBody(e.target.value)} />
            <p className="mt-1 text-xs text-foreground-500">
              Variables disponibles: {"{nombre}"}, {"{email}"}, {"{telefono}"}, {"{tipo_unidad}"}, {"{m2}"}, {"{fecha_inicio}"},{" "}
              {"{mensaje}"} y {"{sitio}"}. Para evitar abusos, a cada dirección solo se le responde una vez cada 24 horas.
            </p>
          </div>
        </section>

        <button
          type="button"
          onClick={handleSave}
          disabled={busy !== null}
          className="rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
        >
          {busy === "save" ? "Guardando..." : "Guardar configuración"}
        </button>
      </div>
    </AdminLayout>
  );
}
